import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import media from 'src/styles/media';

import imgArrow from 'src/assets/images/arrow-right.svg';
import imgClose from 'src/assets/images/close-black.svg';
import imgBgCircle from 'src/assets/images/bg-circle-grey.svg';

const Popup = ({
  shown,
  currentUnit,
  closePopup,
  handlePrevFloorplan,
  handleNextFloorplan,
  dataFloorplans,
}) => (
  <Root shown={shown}>
    <Close onClick={closePopup}>
      <img src={imgClose} alt="Close" />
    </Close>
    <Content key={currentUnit}>
      <BackgroundCircle image={imgBgCircle} />
      <Info>
        <h1>{dataFloorplans[currentUnit].id}</h1>
        <h3>
          {dataFloorplans[currentUnit].junior && 'Junior '}
          {dataFloorplans[currentUnit].bed === 0
            ? 'Studio '
            : dataFloorplans[currentUnit].bed + ' Bed '}
          {dataFloorplans[currentUnit].den && '+ Den'}
          {dataFloorplans[currentUnit].flex && '+ Flex'}
          {dataFloorplans[currentUnit].adaptable && ' (A)'}
          {dataFloorplans[currentUnit].family && ' + Family Room'}
        </h3>
        {dataFloorplans[currentUnit].adaptable && (
          <p className="adapt">Adaptable Unit</p>
        )}
        <SizeContent>
          {dataFloorplans[currentUnit].interior && (
            <p>
              {dataFloorplans[currentUnit].interior} sq ft
              <br />
              Interior
            </p>
          )}
          {dataFloorplans[currentUnit].exterior && (
            <p>
              {dataFloorplans[currentUnit].exterior} sq ft
              <br />
              Exterior
            </p>
          )}
        </SizeContent>
        <p className="total-size">
          {dataFloorplans[currentUnit].size} sq ft
          <br />
          {currentUnit.includes('TH') ? 'Total' : 'Interior'}
        </p>
        <Links>
          <StyledLink
            href={`/assets/images/floorplans/618_${dataFloorplans[currentUnit].img}.pdf`}
            target="_blank"
          >
            Download
          </StyledLink>
          <StyledButton back={true} onClick={closePopup}>
            <img src={imgArrow} alt="Back to Selection" />
            Back to Selection
          </StyledButton>
        </Links>
        <Levels
          src={require(`src/assets/images/floorplans/floorplates/${dataFloorplans[currentUnit].img}@2x.png`)}
        />
      </Info>
      <Image soldout={dataFloorplans[currentUnit].soldout}>
        <Wrapper>
          <img
            src={require(`src/assets/images/floorplans/plans/${currentUnit.toLowerCase()}@2x.png`)}
            alt={dataFloorplans[currentUnit].id}
            className="plan"
          />
          {dataFloorplans[currentUnit].soldout && (
            <Sold
              src={require('src/assets/images/floorplans/sold_out.png')}
              alt="sold out"
            />
          )}
        </Wrapper>
        <Navigation>
          <a onClick={() => handlePrevFloorplan(currentUnit)}>
            <img src={imgArrow} alt="Previous Plan" />
            Previous Plan
          </a>
          <a onClick={() => handleNextFloorplan(currentUnit)}>
            Next Plan
            <img src={imgArrow} alt="Next Plan" />
          </a>
        </Navigation>
      </Image>
    </Content>
  </Root>
);

export default Popup;

Popup.propTypes = {
  shown: PropTypes.bool,
  currentUnit: PropTypes.string,
  closePopup: PropTypes.func,
  handlePrevFloorplan: PropTypes.func,
  handleNextFloorplan: PropTypes.func,
  dataFloorplans: PropTypes.object,
};

const Root = styled.div`
  position: fixed;
  background-color: white;
  top: 0;
  height: 100vh;
  width: 100vw;
  transition: 0.3s ease;
  z-index: 505;
  transform: ${(props) =>
    props.shown ? 'translateY(0)' : 'translateY(100vh)'};
  padding: 30px 5%;
  @media (min-width: ${media.mdup}) {
    padding: 5%;
  }
`;

const fadeIn = keyframes`
    0% {
      display: none;
    }
    1% {
      display: block;
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  animation: 0.5s ease ${fadeIn} forwards;
  height: 90%;
  @media (min-width: ${media.mdup}) {
    justify-content: space-between;
    height: 100%;
  }
  h1 {
    font-size: 50px;
    text-transform: uppercase;
    text-align: left;
  }
  h3 {
    color: ${({ theme }) => theme.colorPrimary};
    font-family: ${({ theme }) => theme.fontHeader};
    text-transform: uppercase;
    font-size: 20px;
    font-weight: 500;
  }
  p {
    font-size: 18px;
    font-family: ${({ theme }) => theme.fontHeader};
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.72);
    margin: 2% 0;
    @media (min-width: ${media.mdup}) {
      margin: 20px 0;
    }
  }
  p.adapt {
    text-transform: none;
    margin: 0;
  }
  p.total-size {
    margin-bottom: 2%;
    color: ${({ theme }) => theme.colorDarkBlue};
    @media (min-width: ${media.mdup}) {
      margin-bottom: 50px;
    }
  }
`;

const Links = styled.div`
  display: flex;
  margin: 4% 0;
  @media (min-width: ${media.mdup}) {
    display: block;
    margin: 0;
  }
`;

const SizeContent = styled.div`
  display: flex;
  p {
    margin-right: 5%;
    margin-bottom: 0;
  }
`;

const Info = styled.div`
  width: 100%;
  max-height: 50%;
  @media (min-width: ${media.mdup}) {
    width: 30%;
  }
`;

const BackgroundCircle = styled.div`
  transform: translate(-50%, -50%);
  background-image: url(${(props) => props.image});
  background-size: cover;
  width: 30vw;
  height: 30vw;
  position: absolute;
  top: 90vh;
  left: 100vw;
  z-index: -10;
  display: none;
  @media (min-width: ${media.smup}) {
    display: block;
  }
`;

const Image = styled.div`
  width: 100%;
  height: 35vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (min-width: ${media.mdup}) {
    width: 70%;
    height: 100%;
  }
  .plan {
    max-height: 35vh;
    max-width: 100%;
    z-index: 10;
    position: relative;
    opacity: ${(props) => (props.soldout ? 0.3 : 1)};
    @media (min-width: ${media.mdup}) {
      max-height: 70vh;
    }
  }
`;

const Sold = styled.img`
  width: 90px;
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 11;
  opacity: 1;
  @media (min-width: ${media.mdup}) {
    width: 117px;
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

const Levels = styled.img`
  display: none;
  @media (min-width: ${media.mdup}) {
    display: block;
    max-width: 100%;
    max-height: 40vh;
  }
`;

const Close = styled.button`
  position: absolute;
  right: 5%;
  top: 2%;
  font-size: 30px;
  cursor: pointer;
  @media (min-width: ${media.mdup}) {
    top: 5%;
  }
`;

const StyledLink = styled.a`
  height: 35px;
  padding: 0 21px;
  max-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
  border-radius: 17.5px;
  background-color: white;
  font-family: ${({ theme }) => theme.fontHeader};
  font-size: 12px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.83);
  transition: 0.3s ease;
  margin-right: 20px;
  cursor: pointer;
  @media (min-width: ${media.mdup}) {
    display: flex;
  }
  &:hover {
    background-color: ${({ theme }) => theme.colorPrimary};
  }
`;

const StyledButton = styled.button`
  display: block;
  height: 35px;
  padding: 0 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
  border-radius: 17.5px;
  background-color: white;
  font-family: ${({ theme }) => theme.fontHeader};
  font-size: 12px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.83);
  transition: 0.3s ease;
  display: inline-block;
  cursor: pointer;
  @media (min-width: ${media.mdup}) {
    display: block;
    margin: 20px 0;
  }
  &:hover {
    background-color: ${({ theme }) => theme.colorPrimary};
  }
  img {
    transform: rotate(180deg);
    margin-right: 15px;
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  bottom: 10%;
  font-family: ${({ theme }) => theme.fontHeader};
  @media (min-width: ${media.mdup}) {
    bottom: 7%;
  }
  a:first-child {
    img {
      transform: rotate(180deg);
    }
  }
  a {
    margin: 0 15px;
    display: flex;
    align-items: center;
    text-transform: uppercase;
    cursor: pointer;
    img {
      margin: 0 5px;
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/Floorplans/elements/Popup.js